"use client";

import type { Selection } from "@react-types/shared";

import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/modal";
import { Select, SelectItem, type SelectProps } from "@heroui/select";
import Image from "next/image";
import { usePathname } from "next/navigation";

import type { LanguageCode } from "@/config/i18n";
import type { TranslationKey } from "@/lib/i18n/translations";
import type { MovieFilters } from "@/types/movie";
import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";
import { clearFilters, countActiveFilters } from "@/lib/movies/browse-params";
import { cn } from "@/lib/utils";

interface FilterPanelProps {
  filters: MovieFilters;
  onChange: (filters: MovieFilters) => void;
  availableGenres: string[];
  yearRange: { min: number; max: number };
  /** Number of movies matching the current filters (shown on the close button). */
  resultCount?: number;
  /** Defaults to the language in the current URL. */
  lang?: LanguageCode;
  /** Extra classes for the trigger button. */
  className?: string;
}

type RangeKey = keyof Pick<
  MovieFilters,
  | "imdbMin"
  | "imdbMax"
  | "rottenTomatoesCriticMin"
  | "rottenTomatoesCriticMax"
  | "rottenTomatoesAudienceMin"
  | "rottenTomatoesAudienceMax"
  | "ratingMin"
  | "ratingMax"
  | "cageMdbMin"
  | "cageMdbMax"
>;

interface RatingRange {
  id: string;
  /** Accessible name of the rating, e.g. "IMDb Rating". */
  labelKey: TranslationKey;
  /** Visible heading, e.g. "IMDb Rating (0-10)". */
  headingKey: TranslationKey;
  logo: string;
  logoClassName: string;
  min: RangeKey;
  max: RangeKey;
  limit: number;
  step?: number;
}

const RATING_RANGES: RatingRange[] = [
  {
    id: "imdb",
    labelKey: "movies.imdbRating",
    headingKey: "movies.imdbRatingRange",
    logo: "/IMDB-logo.svg",
    logoClassName: "h-4 w-auto",
    min: "imdbMin",
    max: "imdbMax",
    limit: 10,
    step: 0.1,
  },
  {
    id: "rt-critic",
    labelKey: "movies.rtCriticRating",
    headingKey: "movies.rtCriticRatingRange",
    logo: "/rotten-tomatoes-critic-logo.svg",
    logoClassName: "h-4 w-auto",
    min: "rottenTomatoesCriticMin",
    max: "rottenTomatoesCriticMax",
    limit: 100,
  },
  {
    id: "rt-audience",
    labelKey: "movies.rtAudienceRating",
    headingKey: "movies.rtAudienceRatingRange",
    logo: "/rotten-tomatoes-audience-logo.svg",
    logoClassName: "h-4 w-auto",
    min: "rottenTomatoesAudienceMin",
    max: "rottenTomatoesAudienceMax",
    limit: 100,
  },
  {
    id: "tmdb",
    labelKey: "movies.tmdbRating",
    headingKey: "movies.tmdbRatingRange",
    logo: "/tmdb-logo.svg",
    logoClassName: "h-4 w-auto",
    min: "ratingMin",
    max: "ratingMax",
    limit: 10,
    step: 0.1,
  },
  {
    id: "cage-mdb",
    labelKey: "movies.cageMdbRating",
    headingKey: "movies.cageMdbRatingRange",
    logo: "/nicolas-cage.png",
    logoClassName: "h-5 w-4",
    min: "cageMdbMin",
    max: "cageMdbMax",
    limit: 10,
    step: 0.1,
  },
];

const inputClassNames = { inputWrapper: "h-12 min-h-12" };

/**
 * While the sheet is open, everything outside it is hidden from screen
 * readers, and a dropdown's list renders outside it (at the end of <body>):
 * react-aria's top-layer marker keeps the list readable.
 */
const sheetDropdownPopoverProps = {
  classNames: { content: "max-w-[calc(100vw-2rem)]" },
  "data-react-aria-top-layer": "true",
} as SelectProps["popoverProps"];

function toNumber(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const number = Number(value);

  return Number.isFinite(number) ? number : undefined;
}

/**
 * Filters button + dialog. The dialog is a bottom sheet on phones and a
 * centered panel on larger screens; filters apply as you change them.
 */
export function FilterPanel({
  filters,
  onChange,
  availableGenres,
  yearRange,
  resultCount,
  lang,
  className,
}: FilterPanelProps) {
  const pathname = usePathname();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const t = createTranslator(lang ?? getLanguageFromPathname(pathname));
  const activeCount = countActiveFilters(filters);
  const filtersActive = activeCount > 0;

  const handleGenreChange = (keys: Selection) => {
    onChange({
      ...filters,
      genres: keys === "all" ? [] : Array.from(keys as Set<string>),
    });
  };

  const setNumber = (key: keyof MovieFilters, value: string) => {
    onChange({ ...filters, [key]: toNumber(value) });
  };

  return (
    <>
      <Button
        aria-haspopup="dialog"
        aria-label={
          filtersActive
            ? t("movies.filtersWithCount", { count: activeCount })
            : t("movies.filters")
        }
        // Icon-only below 360px so the sort control keeps room for its value
        className={cn("h-14 min-w-14 px-4", className)}
        startContent={
          <svg
            aria-hidden="true"
            className="shrink-0"
            fill="none"
            focusable="false"
            height="1.1em"
            role="presentation"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="1.1em"
          >
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
        }
        variant="flat"
        onPress={onOpen}
      >
        <span className="hidden font-semibold min-[360px]:inline">
          {t("movies.filters")}
        </span>
        {filtersActive && (
          <span
            aria-hidden="true"
            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground"
          >
            {activeCount}
          </span>
        )}
      </Button>

      <Modal
        classNames={{
          base: "max-w-[calc(100vw-0.5rem)] sm:max-w-lg",
          closeButton:
            "top-2 end-2 flex min-h-11 min-w-11 items-center justify-center",
          footer: "pb-[max(1rem,env(safe-area-inset-bottom))]",
        }}
        isOpen={isOpen}
        placement="auto"
        scrollBehavior="inside"
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center justify-between gap-2 border-b border-default-100 pe-14">
                <span>{t("movies.filterOptions")}</span>
                <Button
                  className="min-h-11"
                  color="danger"
                  isDisabled={!filtersActive}
                  size="sm"
                  variant="light"
                  onPress={() => onChange(clearFilters(filters))}
                >
                  {t("movies.clearAll")}
                </Button>
              </ModalHeader>
              <ModalBody className="gap-5 pb-4">
                {/* Genre Filter */}
                <Select
                  classNames={{ trigger: "min-h-12" }}
                  id="genre-filter"
                  label={t("movies.genres")}
                  // Escape closes the list (by default it would first clear
                  // the picked genres; "Clear all" is for that)
                  listboxProps={{ escapeKeyBehavior: "none" }}
                  name="genres"
                  placeholder={t("movies.selectGenres")}
                  popoverProps={sheetDropdownPopoverProps}
                  selectedKeys={new Set(filters.genres ?? [])}
                  selectionMode="multiple"
                  onSelectionChange={handleGenreChange}
                >
                  {availableGenres.map((genre) => (
                    <SelectItem key={genre}>{genre}</SelectItem>
                  ))}
                </Select>

                {/* Year Range */}
                <fieldset className="space-y-2">
                  <legend className="mb-2 text-sm font-medium">
                    {t("movies.releaseYear")}
                  </legend>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      aria-label={t("movies.yearFromLabel")}
                      classNames={inputClassNames}
                      id="year-from"
                      inputMode="numeric"
                      max={yearRange.max}
                      min={yearRange.min}
                      name="yearFrom"
                      placeholder={t("movies.from")}
                      type="number"
                      value={filters.yearFrom?.toString() ?? ""}
                      onValueChange={(value) => setNumber("yearFrom", value)}
                    />
                    <Input
                      aria-label={t("movies.yearToLabel")}
                      classNames={inputClassNames}
                      id="year-to"
                      inputMode="numeric"
                      max={yearRange.max}
                      min={yearRange.min}
                      name="yearTo"
                      placeholder={t("movies.to")}
                      type="number"
                      value={filters.yearTo?.toString() ?? ""}
                      onValueChange={(value) => setNumber("yearTo", value)}
                    />
                  </div>
                </fieldset>

                {RATING_RANGES.map((range) => {
                  const label = t(range.labelKey);

                  return (
                    <fieldset key={range.id} className="space-y-2">
                      <legend className="mb-2 flex items-center gap-2 text-sm font-medium">
                        <Image
                          alt=""
                          className={range.logoClassName}
                          height={24}
                          src={range.logo}
                          width={24}
                        />
                        <span>{t(range.headingKey)}</span>
                      </legend>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          aria-label={t("movies.minimumOf", { label })}
                          classNames={inputClassNames}
                          id={`${range.id}-min`}
                          inputMode="decimal"
                          max={range.limit}
                          min={0}
                          name={range.min}
                          placeholder={t("movies.min")}
                          step={range.step}
                          type="number"
                          value={filters[range.min]?.toString() ?? ""}
                          onValueChange={(value) => setNumber(range.min, value)}
                        />
                        <Input
                          aria-label={t("movies.maximumOf", { label })}
                          classNames={inputClassNames}
                          id={`${range.id}-max`}
                          inputMode="decimal"
                          max={range.limit}
                          min={0}
                          name={range.max}
                          placeholder={t("movies.max")}
                          step={range.step}
                          type="number"
                          value={filters[range.max]?.toString() ?? ""}
                          onValueChange={(value) => setNumber(range.max, value)}
                        />
                      </div>
                    </fieldset>
                  );
                })}
              </ModalBody>
              <ModalFooter className="border-t border-default-100">
                <Button
                  className="min-h-12 w-full font-semibold sm:w-auto"
                  color="primary"
                  onPress={onClose}
                >
                  {resultCount === undefined
                    ? t("movies.filters")
                    : t("movies.showResults", { count: resultCount })}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
